import React, { useState, useRef, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { canDeleteStatus } from '../../../FlowchartView/utils/statusUtils';
import AddItemPopup from './AddItemPopup';

/**
 * ColumnHeader Component
 * Handles the column header with drag handle, label editing, and action buttons
 */
const ColumnHeader = memo(({
  column,
  taskCount,
  statuses,
  dragListeners,
  onColumnNameSave,
  onToggleCollapse,
  onAddTask,
  onAddDecision,
  onAddWait,
  onOpenDeleteModal,
  setEditingCardId,
  onSetWipLimit,
}) => {
  // Local state for name editing
  const [editValue, setEditValue] = useState(column.name);
  const [isEditing, setIsEditing] = useState(false);
  const nameInputRef = useRef(null);
  const nameLabelRef = useRef(null);
  const clickCursorPosRef = useRef(null);
  
  // State for add item popup
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const addButtonRef = useRef(null);
  
  // State for WIP limit editor
  const [isWipEditorOpen, setIsWipEditorOpen] = useState(false);
  const [wipInputValue, setWipInputValue] = useState('');
  const [wipEditorPosition, setWipEditorPosition] = useState(null);
  const countRef = useRef(null);
  const wipInputRef = useRef(null);
  
  // WIP limit info
  const wipLimit = column.wipLimit;
  const hasWipLimit = wipLimit !== null && wipLimit !== undefined;
  const isAtLimit = hasWipLimit && taskCount === wipLimit;
  const isOverLimit = hasWipLimit && taskCount > wipLimit;
  const isBlockedColumn = column.category === 'blocked';

  // Focus input when editing starts and set cursor position from click
  useEffect(() => {
    if (isEditing && nameInputRef.current) {
      nameInputRef.current.focus();
      // Set cursor position from click, or end if not available
      const pos = clickCursorPosRef.current ?? editValue.length;
      nameInputRef.current.setSelectionRange(pos, pos);
      clickCursorPosRef.current = null; // Reset for next time
    }
  }, [isEditing, editValue.length]);

  // Update edit value when column name changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditValue(column.name);
    }
  }, [column.name, isEditing]);
  
  // Focus WIP input when editor opens
  useEffect(() => {
    if (isWipEditorOpen && wipInputRef.current) {
      wipInputRef.current.focus();
      wipInputRef.current.select();
    }
  }, [isWipEditorOpen]);
  
  // Close WIP editor on outside click
  useEffect(() => {
    if (!isWipEditorOpen) return;
    
    const handleClickOutside = (e) => {
      if (countRef.current && !countRef.current.contains(e.target)) {
        setIsWipEditorOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isWipEditorOpen]);

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsEditing(false);
      onColumnNameSave?.(column.id, editValue);
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(column.name);
      onColumnNameSave?.(column.id, column.name);
    }
  };

  const handleNameBlur = () => {
    setIsEditing(false);
    onColumnNameSave?.(column.id, editValue);
  };

  const handleLabelClick = (e) => {
    e.stopPropagation();
    
    // Calculate cursor position from click location (like Notion)
    if (nameLabelRef.current && document.caretRangeFromPoint) {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      if (range && nameLabelRef.current.contains(range.startContainer)) {
        clickCursorPosRef.current = range.startOffset;
      } else {
        clickCursorPosRef.current = column.name.length;
      }
    } else {
      clickCursorPosRef.current = column.name.length;
    }
    
    setIsEditing(true);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    const deleteCheck = canDeleteStatus(column.id, statuses);
    if (!deleteCheck.canDelete) {
      alert(deleteCheck.reason);
      return;
    }
    onOpenDeleteModal();
  };
  
  // WIP limit editor handlers
  const handleCountClick = (e) => {
    e.stopPropagation();
    // Don't allow WIP limits on blocked columns
    if (isBlockedColumn) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    setWipEditorPosition({
      top: rect.bottom + 4,
      left: rect.left,
    });
    setWipInputValue(hasWipLimit ? String(wipLimit) : '');
    setIsWipEditorOpen(true);
  };
  
  const handleWipSave = () => {
    const value = wipInputValue.trim();
    if (value === '') {
      // Clear the limit
      onSetWipLimit?.(column.id, null);
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 0) {
        onSetWipLimit?.(column.id, numValue);
      }
    }
    setIsWipEditorOpen(false);
  };
  
  const handleWipKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleWipSave();
    } else if (e.key === 'Escape') {
      setIsWipEditorOpen(false);
    }
  };
  
  const handleWipClear = () => {
    onSetWipLimit?.(column.id, null);
    setIsWipEditorOpen(false);
  };

  return (
    <div 
      className="kanban-column__header"
      {...(dragListeners || {})}
    >
      <div className="kanban-column__header-top">
        <span className="kanban-column__drag-handle" title="Drag to reorder">
          <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
            <circle cx="2" cy="2" r="1.5" />
            <circle cx="8" cy="2" r="1.5" />
            <circle cx="2" cy="7" r="1.5" />
            <circle cx="8" cy="7" r="1.5" />
            <circle cx="2" cy="12" r="1.5" />
            <circle cx="8" cy="12" r="1.5" />
          </svg>
        </span>
        <span 
          className="kanban-column__color-dot" 
          style={{ background: column.color }}
        />
        {isEditing ? (
          <input
            ref={nameInputRef}
            type="text"
            className="kanban-column__label-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              handleNameKeyDown(e);
            }}
            onBlur={handleNameBlur}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        ) : (
          <span 
            ref={nameLabelRef}
            className="kanban-column__label"
            onClick={handleLabelClick}
            onMouseDown={(e) => e.stopPropagation()}
            title="Click to rename"
          >
            {column.name}
          </span>
        )}
        <span 
          ref={countRef}
          className={[
            'kanban-column__count',
            hasWipLimit && 'kanban-column__count--has-limit',
            isAtLimit && 'kanban-column__count--at-limit',
            isOverLimit && 'kanban-column__count--over-limit',
            !isBlockedColumn && 'kanban-column__count--clickable',
          ].filter(Boolean).join(' ')}
          onClick={handleCountClick}
          onMouseDown={(e) => e.stopPropagation()}
          title={isBlockedColumn ? 'Blocked columns cannot have WIP limits' : 'Click to set WIP limit'}
        >
          {hasWipLimit ? `${taskCount}/${wipLimit}` : taskCount}
        </span>
        <button 
          className="kanban-column__collapse-btn"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse?.(column.id);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          title="Collapse column"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="11 17 6 12 11 7" />
            <polyline points="18 17 13 12 18 7" />
          </svg>
        </button>
        <button 
          className="kanban-column__delete-btn"
          onClick={handleDeleteClick}
          onPointerDown={(e) => e.stopPropagation()}
          title="Delete column"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
        {/* Add item button with popup */}
        <div className="kanban-column__add-wrapper">
          <button 
            ref={addButtonRef}
            className="kanban-column__add-trigger"
            onClick={(e) => {
              e.stopPropagation();
              setIsAddPopupOpen(!isAddPopupOpen);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            title="Add item"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <AddItemPopup
            isOpen={isAddPopupOpen}
            onClose={() => setIsAddPopupOpen(false)}
            buttonRef={addButtonRef}
            onAddMission={async () => {
              const newTaskId = await onAddTask?.(column.id);
              if (newTaskId) {
                setEditingCardId(newTaskId);
              }
            }}
            onAddDecision={async () => {
              const newTaskId = await onAddDecision?.(column.id);
              if (newTaskId) {
                setEditingCardId(newTaskId);
              }
            }}
            onAddWait={async () => {
              const newTaskId = await onAddWait?.(column.id);
              if (newTaskId) {
                setEditingCardId(newTaskId);
              }
            }}
          />
        </div>
      </div>
      
      {/* WIP Limit Editor Popup */}
      {isWipEditorOpen && wipEditorPosition && createPortal(
        <div 
          className="kanban-column__wip-editor"
          style={{
            position: 'fixed',
            top: wipEditorPosition.top,
            left: wipEditorPosition.left,
            zIndex: 10000,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="kanban-column__wip-editor-header">
            <span>WIP Limit</span>
          </div>
          <div className="kanban-column__wip-editor-body">
            <input
              ref={wipInputRef}
              type="number"
              min="0"
              className="kanban-column__wip-input"
              value={wipInputValue}
              onChange={(e) => setWipInputValue(e.target.value)}
              onKeyDown={handleWipKeyDown}
              placeholder="No limit"
            />
            <div className="kanban-column__wip-actions">
              <button 
                className="kanban-column__wip-btn kanban-column__wip-btn--save"
                onClick={handleWipSave}
              >
                Save
              </button>
              {hasWipLimit && (
                <button 
                  className="kanban-column__wip-btn kanban-column__wip-btn--clear"
                  onClick={handleWipClear}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

export default ColumnHeader;

